import type { ReactNode } from "react";
import { cn } from "../../lib/utils";
import { getInitials } from "../../lib/initials";

const SIZES = {
  sm: "size-8 text-label-sm",
  md: "size-10 text-label-md",
  lg: "size-14 text-headline-md",
} as const;

/**
 * Initials avatar for a person. The data model has no profile image yet, so
 * this renders initials in a neutral circle; pass `imageUrl` once images exist
 * and it'll show the photo instead (initials remain the fallback). A neutral
 * grey (not a brand tint) keeps the initials legible everywhere — including on
 * the white expense form and inside the selected, purple-filled payer pill,
 * where the old pale-purple chip washed out.
 * Decorative by default (`aria-hidden`) since the name is normally shown
 * alongside — pass `label` to give it an accessible name when it stands alone.
 */
export function Avatar({
  name,
  imageUrl,
  size = "md",
  label,
  className,
}: {
  name: string | undefined;
  imageUrl?: string | null;
  size?: keyof typeof SIZES;
  label?: string;
  className?: string;
}): ReactNode {
  const initials = getInitials(name) || "?";

  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt={label ?? name ?? ""}
        className={cn(
          "shrink-0 rounded-full object-cover",
          SIZES[size],
          className,
        )}
      />
    );
  }

  return (
    <span
      aria-hidden={label ? undefined : "true"}
      role={label ? "img" : undefined}
      aria-label={label}
      className={cn(
        "grid shrink-0 place-items-center rounded-full bg-surface-container-highest text-on-surface-variant",
        SIZES[size],
        className,
      )}
    >
      {initials}
    </span>
  );
}
